import { useState, useEffect } from "react";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import style from "./ScoringEvaluationDialog.module.css";

const DIMENSION_ANCHORS = {
  taskClarity: {
    question: "Is it immediately obvious what the user should do next?",
    1: "No clear primary action; multiple competing CTAs or unclear copy",
    2: "Primary action exists but requires reading/thinking to identify",
    3: "Primary action is visible but secondary actions distract",
    4: "Clear primary action with minimal distraction",
    5: "Single, unmistakable primary action; intent is obvious at a glance"
  },
  cognitiveLoad: {
    question: "How mentally demanding is this step?",
    1: "Many choices, dense content, unfamiliar concepts",
    2: "Several decisions or heavy text",
    3: "Moderate amount of information and choice",
    4: "Limited choices, scannable content",
    5: "One decision, minimal text, simple mental model"
  },
  errorPrevention: {
    question: "How well does the step prevent errors or help recover from them?",
    1: "Errors likely; no guidance or recovery",
    2: "Errors shown after failure, vague messaging",
    3: "Clear errors but limited prevention",
    4: "Inline validation or helpful recovery",
    5: "Errors unlikely; strong prevention and clear recovery paths"
  },
  consistency: {
    question: "Does this behave and look like the rest of the product?",
    1: "Breaks design system or interaction patterns",
    2: "Partial reuse; noticeable inconsistencies",
    3: "Mostly consistent with minor deviations",
    4: "Fully consistent with known patterns",
    5: "Consistent and reinforces learned behaviour"
  },
  efficiency: {
    question: "How quickly can a user complete this step?",
    1: "Unnecessary actions or repeated input",
    2: "More actions than expected",
    3: "Acceptable number of actions",
    4: "Minimal required actions",
    5: "Optimised: defaults, shortcuts, or automation"
  },
  feedback: {
    question: "Does the system clearly communicate what's happening?",
    1: "No feedback; user left guessing",
    2: "Delayed or unclear feedback",
    3: "Basic feedback after action",
    4: "Timely and clear feedback",
    5: "Immediate, contextual, reassuring feedback"
  }
};

const DIMENSIONS = [
  { key: "taskClarity", label: "Task Clarity" },
  { key: "cognitiveLoad", label: "Cognitive Load" },
  { key: "errorPrevention", label: "Error Prevention & Recovery" },
  { key: "consistency", label: "Consistency" },
  { key: "efficiency", label: "Efficiency" },
  { key: "feedback", label: "Feedback & System Status" },
];

const SCORE_OPTIONS = [1, 2, 3, 4, 5, "N/A"];

const ScoringEvaluationDialog = ({
  show,
  onClose,
  cellId,
  rowId,
  journeyRefURL,
  userId,
  userName,
  existingEvaluation,
  onSave,
  pictureURL,
}) => {
  const [dimensions, setDimensions] = useState({
    taskClarity: null,
    cognitiveLoad: null,
    errorPrevention: null,
    consistency: null,
    efficiency: null,
    feedback: null,
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    if (existingEvaluation && existingEvaluation.dimensions) {
      setDimensions(existingEvaluation.dimensions);
    } else {
      setDimensions({
        taskClarity: null,
        cognitiveLoad: null,
        errorPrevention: null,
        consistency: null,
        efficiency: null,
        feedback: null,
      });
    }
    // Reset to first question when dialog opens
    if (show) {
      setCurrentQuestionIndex(0);
    }
  }, [existingEvaluation, show]);

  function handleDimensionChange(dimensionKey, value) {
    setDimensions((prev) => ({
      ...prev,
      [dimensionKey]: value === "N/A" ? "N/A" : value === "" ? null : Number(value),
    }));
  }

  function handleSave() {
    const evaluation = {
      designerId: userId,
      designerName: userName,
      cellId,
      rowId,
      dimensions,
    };
    onSave(evaluation);
    onClose();
  }

  function handleCancel() {
    onClose();
  }

  function handlePrevious() {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }

  function handleNext() {
    if (currentQuestionIndex < DIMENSIONS.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }

  const currentDimension = DIMENSIONS[currentQuestionIndex];
  const anchors = DIMENSION_ANCHORS[currentDimension.key];

  return (
    <Dialog show={show} onClose={handleCancel} variants={["large"]} title="Step Evaluation">
      <div className={style.dialogContent}>
        <div className={`${style.twoColumnLayout} ${pictureURL ? style.withPicture : style.withoutPicture}`}>
          {/* Picture Panel - Left */}
          {pictureURL && (
            <div className={style.picturePanel}>
              <div className={style.pictureContainer}>
                <img src={pictureURL} alt="Step reference" className={style.referenceImage} />
              </div>
            </div>
          )}

          {/* Questions Panel - Right */}
          <div className={style.questionsPanel}>
            <div className={style.progressIndicator}>
              Question {currentQuestionIndex + 1} of {DIMENSIONS.length}
            </div>

            <div className={style.dimensions}>
              <div key={currentDimension.key} className={style.dimension}>
                <h3 className={style.dimensionLabel}>{currentDimension.label}</h3>
                <p className={style.dimensionQuestion}>{anchors.question}</p>
                <div className={style.scoreOptions}>
                  {SCORE_OPTIONS.map((option) => (
                    <label key={option} className={style.scoreOption}>
                      <input
                        type="radio"
                        name={currentDimension.key}
                        value={option}
                        checked={
                          dimensions[currentDimension.key] === option ||
                          (dimensions[currentDimension.key] === null && option === null)
                        }
                        onChange={(e) => handleDimensionChange(currentDimension.key, e.target.value)}
                      />
                      <div className={style.scoreOptionContent}>
                        <span className={style.scoreNumber}>{option}</span>
                        {option !== "N/A" && anchors[option] && (
                          <span className={style.scoreAnchor}>{anchors[option]}</span>
                        )}
                        {option === "N/A" && (
                          <span className={style.scoreAnchor}>Not applicable</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className={style.navigation}>
              <Button
                variants={["text"]}
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </Button>
              {currentQuestionIndex < DIMENSIONS.length - 1 ? (
                <Button variants={["primary"]} onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button variants={["primary"]} onClick={handleSave}>
                  {existingEvaluation ? "Update Evaluation" : "Save Evaluation"}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className={style.actions}>
          <Button variants={["text"]} onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ScoringEvaluationDialog;

